import React, { useState, useEffect } from 'react';
import { X, Euro, Calendar, Building, TrendingUp, Calculator, Copy, Zap } from 'lucide-react';
import { Revenue, Property } from '../../types';
import { storage } from '../../services/storage';
import { useModal } from '../ui/ModalProvider';
import { SearchableSelect } from '../ui/SearchableSelect';

interface AddRevenueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (revenue: Revenue) => Promise<void>;
  propertyId?: string;
  properties?: Property[];
  agencyId?: string;
  year?: number;
  month?: number;
  revenueToEdit?: Revenue | null;
}

export const AddRevenueModal: React.FC<AddRevenueModalProps> = ({
  isOpen,
  onClose,
  onSave,
  propertyId,
  properties = [],
  agencyId,
  year,
  month,
  revenueToEdit
}) => {
  const { toast } = useModal();
  const now = new Date();
  const currentYear = year || now.getFullYear();
  const currentMonth = month || now.getMonth() + 1;

  const [formData, setFormData] = useState<Partial<Revenue>>({
    propertyId: propertyId || '',
    agencyId: agencyId,
    year: currentYear,
    month: currentMonth,
    nightlyRatesTotal: 0,
    taxesCollected: 0,
    cleaningFeesCollected: 0,
    airbnbCommissionRate: 15.5,
    airbnbCommissionAmount: 0,
    bookingCommissionRate: 18.0,
    bookingCommissionAmount: 0,
    otherPlatformCommissions: {},
    grossRevenue: 0,
    totalCommissions: 0,
    netRevenue: 0,
    source: 'MANUAL'
  });

  const [previousRevenue, setPreviousRevenue] = useState<Revenue | null>(null);

  // Charger le revenu du mois précédent pour suggestions
  useEffect(() => {
    if (isOpen && !revenueToEdit && formData.propertyId && formData.year && formData.month) {
      loadPreviousRevenue();
    }
  }, [isOpen, formData.propertyId, formData.year, formData.month]);

  const loadPreviousRevenue = async () => {
    try {
      const prevMonth = formData.month === 1 ? 12 : (formData.month || 1) - 1;
      const prevYear = formData.month === 1 ? (formData.year || currentYear) - 1 : formData.year;
      const revenues = await storage.getRevenue(formData.propertyId, prevYear, prevMonth);
      if (revenues.length > 0) {
        setPreviousRevenue(revenues[0]);
      } else {
        setPreviousRevenue(null);
      }
    } catch (error) {
      setPreviousRevenue(null);
    }
  };

  const copyFromPreviousMonth = () => {
    if (previousRevenue) {
      setFormData({
        ...formData,
        nightlyRatesTotal: previousRevenue.nightlyRatesTotal,
        taxesCollected: previousRevenue.taxesCollected,
        cleaningFeesCollected: previousRevenue.cleaningFeesCollected,
        airbnbCommissionRate: previousRevenue.airbnbCommissionRate,
        bookingCommissionRate: previousRevenue.bookingCommissionRate,
        otherPlatformCommissions: previousRevenue.otherPlatformCommissions || {}
      });
      toast.success('Données du mois précédent copiées');
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (revenueToEdit) {
        setFormData(revenueToEdit);
        setPreviousRevenue(null);
      } else {
        setFormData({
          propertyId: propertyId || '',
          agencyId: agencyId,
          year: currentYear,
          month: currentMonth,
          nightlyRatesTotal: 0,
          taxesCollected: 0,
          cleaningFeesCollected: 0,
          airbnbCommissionRate: 15.5,
          airbnbCommissionAmount: 0,
          bookingCommissionRate: 18.0,
          bookingCommissionAmount: 0,
          otherPlatformCommissions: {},
          grossRevenue: 0,
          totalCommissions: 0,
          netRevenue: 0,
          source: 'MANUAL'
        });
      }
    }
  }, [isOpen, revenueToEdit, propertyId, currentYear, currentMonth, agencyId]);

  // Calculer les totaux automatiquement
  useEffect(() => {
    const nightly = formData.nightlyRatesTotal || 0;
    const taxes = formData.taxesCollected || 0;
    const cleaning = formData.cleaningFeesCollected || 0;
    const gross = nightly + taxes + cleaning;

    const airbnbRate = formData.airbnbCommissionRate || 15.5;
    const bookingRate = formData.bookingCommissionRate || 18.0;
    const airbnbCommission = nightly * (airbnbRate / 100);
    const bookingCommission = nightly * (bookingRate / 100);

    const otherCommissions = Object.values(formData.otherPlatformCommissions || {})
      .reduce((sum, c) => sum + (c.amount || 0), 0);

    const totalCommissions = airbnbCommission + bookingCommission + otherCommissions;
    const net = gross - totalCommissions;

    setFormData(prev => ({
      ...prev,
      grossRevenue: gross,
      airbnbCommissionAmount: airbnbCommission,
      bookingCommissionAmount: bookingCommission,
      totalCommissions,
      netRevenue: net
    }));
  }, [
    formData.nightlyRatesTotal,
    formData.taxesCollected,
    formData.cleaningFeesCollected,
    formData.airbnbCommissionRate,
    formData.bookingCommissionRate,
    formData.otherPlatformCommissions
  ]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.propertyId || !formData.year || !formData.month) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await onSave(formData as Revenue);
      toast.success('Revenus enregistrés avec succès');
      onClose();
    } catch (error: any) {
      toast.error(`Erreur lors de l'enregistrement: ${error.message || 'Erreur inconnue'}`);
    }
  };

  const months = [
    { value: 1, label: 'Janvier' },
    { value: 2, label: 'Février' },
    { value: 3, label: 'Mars' },
    { value: 4, label: 'Avril' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' },
    { value: 8, label: 'Août' },
    { value: 9, label: 'Septembre' },
    { value: 10, label: 'Octobre' },
    { value: 11, label: 'Novembre' },
    { value: 12, label: 'Décembre' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-xl font-bold text-slate-900 flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-amber-500" />
            {revenueToEdit ? 'Modifier les revenus' : 'Nouveaux revenus'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Suggestion depuis mois précédent */}
          {!revenueToEdit && previousRevenue && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center text-blue-900 font-medium mb-1">
                    <Zap className="w-4 h-4 mr-2" />
                    Revenus du mois précédent disponibles
                  </div>
                  <p className="text-sm text-blue-700 mb-2">
                    {months.find(m => m.value === (formData.month === 1 ? 12 : (formData.month || 1) - 1))?.label} {formData.month === 1 ? (formData.year || currentYear) - 1 : formData.year} : {previousRevenue.netRevenue.toFixed(2)} € (nets)
                  </p>
                  <button
                    type="button"
                    onClick={copyFromPreviousMonth}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copier les données du mois précédent
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Bien et Période */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Bien *</label>
              <SearchableSelect
                options={properties.map(p => ({ value: p.id!, label: p.name || p.address }))}
                value={formData.propertyId || ''}
                onChange={(value) => setFormData({ ...formData, propertyId: value })}
                placeholder="Sélectionner un bien"
                icon={<Building className="w-4 h-4" />}
                searchPlaceholder="Rechercher un bien..."
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Année *</label>
              <input
                type="number"
                required
                min="2020"
                max="2100"
                className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900"
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Mois *</label>
              <SearchableSelect
                options={months}
                value={formData.month?.toString() || ''}
                onChange={(value) => setFormData({ ...formData, month: parseInt(value) })}
                placeholder="Sélectionner un mois"
                icon={<Calendar className="w-4 h-4" />}
                searchPlaceholder="Rechercher un mois..."
                showAllOnOpen={true}
              />
            </div>
          </div>

          {/* Ventes détaillées */}
          <div className="bg-slate-50 p-4 rounded-xl space-y-4">
            <h3 className="text-sm font-bold text-slate-700 uppercase">Ventes</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 block">Prix nuitées totales (€)</label>
                <div className="relative">
                  <Euro className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full pl-9 p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900"
                    value={formData.nightlyRatesTotal || 0}
                    onChange={(e) => setFormData({ ...formData, nightlyRatesTotal: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 block">Taxes de séjour collectées (€)</label>
                <div className="relative">
                  <Euro className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full pl-9 p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900"
                    value={formData.taxesCollected || 0}
                    onChange={(e) => setFormData({ ...formData, taxesCollected: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 block">Frais de ménage collectés (€)</label>
                <div className="relative">
                  <Euro className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full pl-9 p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900"
                    value={formData.cleaningFeesCollected || 0}
                    onChange={(e) => setFormData({ ...formData, cleaningFeesCollected: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Commissions plateformes */}
          <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl space-y-4">
            <h3 className="text-sm font-bold text-amber-700 uppercase">Commissions plateformes</h3>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 block">Airbnb % HT</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900"
                  value={formData.airbnbCommissionRate || 15.5}
                  onChange={(e) => setFormData({ ...formData, airbnbCommissionRate: parseFloat(e.target.value) || 15.5 })}
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 block">Montant Airbnb (€)</label>
                <div className="p-2.5 bg-slate-100 border border-slate-200 rounded-lg text-slate-600 font-mono text-sm">
                  {formData.airbnbCommissionAmount?.toFixed(2) || '0.00'}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 block">Booking %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900"
                  value={formData.bookingCommissionRate || 18.0}
                  onChange={(e) => setFormData({ ...formData, bookingCommissionRate: parseFloat(e.target.value) || 18.0 })}
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 block">Montant Booking (€)</label>
                <div className="p-2.5 bg-slate-100 border border-slate-200 rounded-lg text-slate-600 font-mono text-sm">
                  {formData.bookingCommissionAmount?.toFixed(2) || '0.00'}
                </div>
              </div>
            </div>
          </div>

          {/* Totaux calculés */}
          <div className="bg-green-50 border border-green-200 p-4 rounded-xl space-y-2">
            <h3 className="text-sm font-bold text-green-700 uppercase flex items-center">
              <Calculator className="w-4 h-4 mr-2" />
              Totaux calculés
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 block">Revenus bruts (€)</label>
                <div className="p-2.5 bg-white border border-green-200 rounded-lg text-green-700 font-mono font-bold text-lg">
                  {formData.grossRevenue?.toFixed(2) || '0.00'}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 block">Total commissions (€)</label>
                <div className="p-2.5 bg-white border border-green-200 rounded-lg text-red-600 font-mono font-bold text-lg">
                  -{(formData.totalCommissions?.toFixed(2) || '0.00')}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 block">Revenus nets (€)</label>
                <div className="p-2.5 bg-white border border-green-200 rounded-lg text-green-700 font-mono font-bold text-xl">
                  {formData.netRevenue?.toFixed(2) || '0.00'}
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Notes</label>
            <textarea
              className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900"
              rows={3}
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Informations supplémentaires..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors"
            >
              {revenueToEdit ? 'Modifier' : 'Créer'} les revenus
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
